import React, { useMemo, useState } from 'react';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import OpenInNew from '@mui/icons-material/OpenInNew';
import { ResponseErrorPanel } from '@backstage/core-components';
import { Entity } from '@backstage/catalog-model';
import {
  useSonarCloudProjectMetrics,
  useSonarCloudProjects,
} from '../../hooks/useSonarCloudApi';
import { useSonarQubeEntity } from '../../hooks/useSonarQubeEntity';
import { SonarQubeMetricsGrid } from './SonarQubeMetricsGrid';
import { useStyles } from './styles';
import { DateTimeDisplay } from '../DateTimeDisplay';

// DO NOT change this metrics list to fetch here
// They are also used as lookup values in SonarQubeMetricsGrid
const metricList = [
  'violations',
  'security_hotspots',
  'coverage',
  'duplicated_lines_density',
];

const SonarQubeMetricsContainer = ({ entity }: { entity: Entity }) => {
  const classes = useStyles();
  const { projectKey, projectUrl } = useSonarQubeEntity(entity);
  const [sonarMode, setSonarMode] = useState<'new_code' | 'overall'>(
    'new_code',
  );

  const metrics = useMemo(() => {
    if (sonarMode === 'new_code') {
      // New metrics start with 'new_' prefix, by design by SonarQube
      return metricList.map(m => `new_${m}`);
    }

    return metricList;
  }, [sonarMode]);

  const {
    data: sonarMetrics,
    isFetching: sonarMetricsFetching,
    error: sonarMetricsError,
  } = useSonarCloudProjectMetrics(projectKey, metrics);

  const {
    data: sonarProjects,
    isFetching: sonarProjectsFetching,
    error: sonarProjectsError,
  } = useSonarCloudProjects(projectKey);

  const lastAnalysisDate = useMemo(() => {
    if (sonarProjects && sonarProjects.length > 0) {
      const project = sonarProjects.find(p => p.key === projectKey);
      return project?.lastAnalysisDate;
    }
    return undefined;
  }, [sonarProjects, projectKey]);

  return (
    <>
      <Typography variant="h2" className={classes.label}>
        Sonar Cloud
      </Typography>
      <p>
        <Link href={projectUrl} target="_blank" rel="noopener noreferrer">
          <Box display="flex" alignItems="center" gap={2}>
            <OpenInNew fontSize="small" />
            {projectKey}
          </Box>
        </Link>
      </p>
      <Typography variant="h2" className={classes.label}>
        Last Analysis
      </Typography>
      {sonarProjectsError && (
        <ResponseErrorPanel
          title="Error Loading Sonar Projects"
          error={sonarProjectsError}
        />
      )}
      {!sonarProjectsFetching && !sonarProjectsError && (
        <p>
          <DateTimeDisplay date={lastAnalysisDate} />
        </p>
      )}
      <RadioGroup
        aria-label="sonar mode"
        name="sonarMode"
        row
        value={sonarMode}
        onChange={e => setSonarMode(e.target.value as 'new_code' | 'overall')}
      >
        <FormControlLabel
          value="new_code"
          control={<Radio size="small" />}
          label={<span className={classes.label}>New Code</span>}
        />
        <FormControlLabel
          value="overall"
          control={<Radio size="small" />}
          label={<span className={classes.label}>Overall</span>}
        />
      </RadioGroup>
      {sonarMetricsError && (
        <ResponseErrorPanel
          title="Error Loading Sonar Metrics"
          error={sonarMetricsError}
        />
      )}
      {!sonarMetricsError && (
        <p>
          <SonarQubeMetricsGrid
            metrics={sonarMetrics || []}
            projectKey={projectKey}
            isLoading={sonarMetricsFetching}
          />
        </p>
      )}
    </>
  );
};

export { SonarQubeMetricsContainer };
